import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format, subDays, startOfDay, endOfDay, isWithinInterval } from "date-fns";
import { Check, Circle } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { cn } from "@/lib/utils";

export const StreakDisplay = () => {
  const { data: entries, isLoading } = useQuery({
    queryKey: ['entries-for-streak'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('food_entries')
        .select('consumed_at')
        .order('consumed_at', { ascending: false });
      
      if (error) throw error;
      return data;
    },
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center space-x-2 animate-pulse">
        {[...Array(7)].map((_, i) => (
          <div key={i} className="w-8 h-8 bg-gray-200 rounded-full" />
        ))}
      </div>
    );
  }

  if (!entries?.length) {
    return null;
  }

  const last7Days = [...Array(7)].map((_, i) => {
    const date = subDays(new Date(), i);
    const dayStart = startOfDay(date);
    const dayEnd = endOfDay(date);
    
    const hasEntry = entries?.some(entry => 
      isWithinInterval(new Date(entry.consumed_at), { start: dayStart, end: dayEnd })
    );

    return {
      date,
      hasEntry,
    };
  }).reverse();

  const currentStreak = last7Days.reduce((streak, day) => {
    if (day.hasEntry) return streak + 1;
    return 0;
  }, 0);

  return (
    <div className="mb-8">
      <div className="flex flex-col items-center space-y-2">
        <div className="flex items-center justify-center gap-2">
          <span className="text-green-600 text-2xl font-bold">{currentStreak}</span>
          <span className="text-gray-500">day streak</span>
        </div>
        <div className="flex items-center justify-center space-x-2">
          {last7Days.map(({ date, hasEntry }, index) => (
            <TooltipProvider key={index}>
              <Tooltip>
                <TooltipTrigger>
                  <div
                    className={cn(
                      "w-8 h-8 rounded-full flex items-center justify-center transition-all",
                      hasEntry
                        ? "bg-nutrition-100 text-nutrition-700"
                        : "bg-gray-100 text-gray-400"
                    )}
                  >
                    {hasEntry ? (
                      <Check className="w-4 h-4" />
                    ) : (
                      <Circle className="w-4 h-4" />
                    )}
                  </div>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{format(date, 'EEEE, MMM d')}</p>
                  <p className="text-xs text-gray-500">
                    {hasEntry ? 'Meal logged' : 'No meals logged'}
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>
      </div>
    </div>
  );
};